/* eslint-disable max-lines-per-function, max-statements */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { ApiError, errorMessage } from '../services/apiError';
import { chatService } from '../services/chatService';
import type { Project } from '../services/projectService';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { useGetPlanningStatusQuery } from '../store/services/planningRuntimeApi';
import { setProjectTitleConfirmation } from '../store/slices/planningSlice';
import { reconcileAmbiguousPlanningTrigger } from '../utils/planningTriggerReconciliation';

import { recordActionClick } from './AiOverviewPanel/useActionBarClicks';
import { revealPlanningConfirmation } from './planningConfirmationVisibility';
import { notifyProductionStart } from './production/productionStartEvents';
import {
  ProjectTitleConfirmation,
  type ProjectTitleConfirmationController,
  useProjectTitleConfirmation,
} from './ProjectTitleConfirmation';
import { useToast } from './Toast/Toast';
import { useSeenCountdown, useSeenElement, type SeenCountdownState } from './useSeenCountdown';

interface StartRequest {
  actionKey: string;
  buttonKey: string;
  invocationId?: string;
  projectId: string;
  sessionId?: string | null;
  needsTitle: boolean;
  onInvocation: () => Promise<void>;
  onStart: () => Promise<void>;
}

interface PendingStart extends StartRequest {
  invocation: Promise<void>;
  started: boolean;
}

type ChatSurface = 'overview' | 'chat';

/** The current suggestion whose Start will also confirm the project title. */
export interface TitleDraft {
  actionKey: string;
  projectId: string;
  sessionId?: string | null;
}

/** Seconds a seen, untouched title form waits before Start fires on its own. */
export const AUTO_START_SECONDS = 20;

/** The countdown a seen, untouched title form runs before Start fires on its own. */
type AutoStartState = SeenCountdownState;

interface PlanningStartContextValue {
  pending: { actionKey: string; buttonKey: string; invocationId?: string; needsTitle: boolean } | null;
  requestStart: (request: StartRequest) => void;
  confirmation: ProjectTitleConfirmationController | null;
  chatSurface: ChatSurface;
  /** Show the title form ahead of Start for this suggestion (null clears an owner). */
  registerTitleDraft: (draft: TitleDraft | null, actionKey?: string) => void;
  autoStart: AutoStartState;
  markDraftSeen: () => void;
  stopAutoStart: () => void;
  consumeAutoStart: () => boolean;
}

const noop = (): void => undefined;

const sameDraft = (left: TitleDraft | null, right: TitleDraft | null): boolean => (
  left === right || Boolean(left && right && left.actionKey === right.actionKey
    && left.projectId === right.projectId && (left.sessionId ?? null) === (right.sessionId ?? null))
);

const PlanningStartContext = createContext<PlanningStartContextValue | null>(null);

const isRejectedInvocation = (error: unknown): boolean => {
  const status = (error as { status?: number; response?: { status?: number } })?.status
    ?? (error as { response?: { status?: number } })?.response?.status;

  return Boolean(status && status >= 400 && status !== 408 && status !== 429 && status < 500);
};

/** Own the confirmation above transcript rows, which may disappear during SSE updates. */
export const PlanningStartProvider: React.FC<React.PropsWithChildren<{
  projectId: string | null;
  sessionId: string | null;
  chatSurface?: ChatSurface;
}>> = ({ projectId, sessionId, chatSurface = 'overview', children }) => {
  const dispatch = useAppDispatch();
  const toast = useToast();
  const projectTitle = useAppSelector((state) => state.planning.projectTitle);
  const { refetch: refetchPlanningStatus } = useGetPlanningStatusQuery(
    projectId ?? '',
    { skip: !projectId },
  );
  const pendingTitleConfirmation = useAppSelector((state) => (
    state.planning.activeProjectId === projectId
      && state.planning.activeSessionId === sessionId
      ? state.planning.pendingTitleConfirmation
      : null
  ));
  const [pending, setPending] = useState<PendingStart | null>(null);
  const [actionRejected, setActionRejected] = useState(false);
  const [draft, setDraft] = useState<TitleDraft | null>(null);
  const requestRef = useRef<PendingStart | null>(null);
  const active = useRef(true);

  useEffect(() => {
    active.current = true;

    return () => {
      active.current = false;
      requestRef.current = null;
    };
  }, []);

  const start = useCallback(async (request: PendingStart) => {
    if (request.started || requestRef.current !== request || !active.current) return;
    request.started = true;
    let finished = false;
    let prepared = false;

    try {
      await request.invocation.catch((error: unknown) => {
        if (isRejectedInvocation(error) || !active.current || requestRef.current !== request) throw error;
        // Save & Start retries the same unacknowledged receipt request. Its
        // callback retains the original request UUID and selected tasks.
        request.invocation = Promise.resolve().then(request.onInvocation);

        return request.invocation;
      });
      prepared = true;
      // Navigation or cancellation must never start the old project's agents.
      if (active.current && requestRef.current === request) {
        await request.onStart();
        finished = true;
        if (active.current && requestRef.current === request) {
          notifyProductionStart({ projectId: request.projectId, startKey: request.actionKey });
        }
      }
    } catch (error) {
      request.started = false;
      if (request.needsTitle && active.current && requestRef.current === request) {
        const rejected = !prepared && isRejectedInvocation(error);

        setActionRejected(rejected);
        throw new ApiError({
          code: 'PLANNING_START_NOT_PREPARED',
          message: rejected
            ? 'Your title is saved, but planning could not start. Select Refresh actions to load the latest next step.'
            : `Your title is saved. ${errorMessage(error, 'Planning could not be started.')}`
              + ' Select Save & Start to retry.',
        });
      }
      if (active.current) toast.error('We couldn’t start planning. Please try again.');
    } finally {
      if (requestRef.current === request && (finished || !request.needsTitle)) {
        requestRef.current = null;
        if (active.current) setPending(null);
      }
    }
  }, [toast]);

  const requestStart = useCallback((request: StartRequest) => {
    if (!active.current || requestRef.current || request.projectId !== projectId
      || (request.sessionId && request.sessionId !== sessionId)) return;
    const next = { ...request, invocation: Promise.resolve(), started: false };

    // Claim synchronously before the transcript request can replace the row or
    // another physical click can submit the same original suggestion again.
    requestRef.current = next;
    setActionRejected(false);
    next.invocation = Promise.resolve().then(request.onInvocation);
    void next.invocation.catch(() => {});
    setPending(next);
    if (!next.needsTitle) void start(next);
  }, [projectId, sessionId, start]);

  // Restore before paint so the saved task projection cannot flash in place
  // of the title form while a refreshed project snapshot is being applied.
  useLayoutEffect(() => {
    const restored = pendingTitleConfirmation;

    if (
      !restored
      || !projectId
      || (restored.session_id ?? null) !== (sessionId ?? null)
      || requestRef.current
    ) return;

    requestStart({
      actionKey: JSON.stringify([projectId, restored.session_id ?? null, restored.source_entry_id]),
      buttonKey: restored.button_key,
      invocationId: restored.invocation_id,
      projectId,
      sessionId: restored.session_id,
      needsTitle: true,
      onInvocation: () => Promise.resolve(),
      onStart: async () => {
        try {
          const result = await chatService.triggerAgents(
            projectId,
            restored.agents,
            restored.source_entry_id,
            restored.session_id,
            undefined,
            restored.invocation_id,
          );

          if (result.status !== 'triggered') {
            throw new Error(result.reason || 'No agent run was started.');
          }
        } catch (error) {
          const accepted = await reconcileAmbiguousPlanningTrigger(
            error,
            restored.source_entry_id,
            () => refetchPlanningStatus().unwrap(),
          );

          if (!accepted) throw error;
          toast.info('Planning started. The confirmation response was delayed.');
        }
        await recordActionClick(
          restored.source_entry_id,
          'agent_suggestion',
          restored.button_key,
          projectId,
          restored.session_id,
        );
      },
    });
  }, [pendingTitleConfirmation, projectId, refetchPlanningStatus, requestStart, sessionId, toast]);

  const confirmed = useCallback(async (project: Project) => {
    const request = requestRef.current;

    if (!active.current || !request || request.started || project.id !== request.projectId) return;
    dispatch(setProjectTitleConfirmation(project));
    await start(request);
  }, [dispatch, start]);

  // The draft is the suggestion whose Start doubles as the title submit. It
  // stays the controller's identity through the click itself, so the typed
  // title survives the hand-over into the pending request.
  const registerTitleDraft = useCallback((next: TitleDraft | null, actionKey?: string) => {
    if (!active.current) return;
    if (next && next.projectId !== projectId) return;
    setDraft((prev) => {
      if (next) return sameDraft(prev, next) ? prev : next;

      return actionKey && prev?.actionKey !== actionKey ? prev : null;
    });
  }, [projectId]);
  const draftKey = draft ? `${draft.projectId}:${draft.sessionId ?? ''}:${draft.actionKey}` : null;
  // A pending request that still waits for its title keeps counting: the
  // countdown's job is to submit that title. Only an accepted start pauses it.
  const {
    state: autoStart, markSeen: markDraftSeen, stop: stopAutoStart, consume: consumeAutoStart,
  } = useSeenCountdown(draftKey, AUTO_START_SECONDS, Boolean(pending && !pending.needsTitle));

  const inlineTitle = Boolean(draft && (!pending?.needsTitle || pending.actionKey === draft.actionKey));
  const titleOwner = pending?.needsTitle ? pending : draft;
  const confirmation = useProjectTitleConfirmation({
    requestKey: titleOwner?.actionKey ?? null,
    currentTitle: projectTitle,
    projectId: titleOwner?.projectId ?? null,
    sessionId: titleOwner?.sessionId ?? sessionId,
    onConfirmed: confirmed,
    // A definitive rejection cannot be retried with the captured request.
    // Reload all projections and their in-memory selections from server state.
    onRefreshActions: actionRejected ? () => window.location.reload() : undefined,
    inline: inlineTitle,
    onInteract: stopAutoStart,
  });
  const value = useMemo(() => ({
    pending, requestStart, chatSurface,
    confirmation: titleOwner ? confirmation : null,
    registerTitleDraft, autoStart, markDraftSeen, stopAutoStart, consumeAutoStart,
  }), [
    pending, requestStart, chatSurface, confirmation, titleOwner,
    registerTitleDraft, autoStart, markDraftSeen, stopAutoStart, consumeAutoStart,
  ]);

  return (
    <PlanningStartContext.Provider value={value}>
      {children}
    </PlanningStartContext.Provider>
  );
};

export const usePlanningStart = (): PlanningStartContextValue => {
  const context = useContext(PlanningStartContext);

  if (!context) throw new Error('Planning actions require PlanningStartProvider');

  return context;
};

/** Read the optional pending request from transcript renderers that may also
 * be mounted in isolated previews outside the planning provider. */
export const usePendingPlanningStart = (): PlanningStartContextValue['pending'] => (
  useContext(PlanningStartContext)?.pending ?? null
);

export const usePlanningStartMessageVisible = (surface: ChatSurface = 'overview'): boolean => {
  const context = useContext(PlanningStartContext);

  return Boolean(context?.confirmation && context.chatSurface === surface);
};

/** A single inline message on the visible chat surface, independent of transcript rows. */
export const PlanningStartMessage: React.FC<{ surface?: ChatSurface }> = ({ surface = 'overview' }) => {
  const context = useContext(PlanningStartContext);
  const messageRef = useRef<HTMLDivElement>(null);
  const visible = Boolean(context?.confirmation && context.chatSurface === surface);

  const inline = Boolean(context?.confirmation?.inline);
  const markDraftSeen = context?.markDraftSeen;

  // The countdown only starts once the reader has actually had the form in
  // front of them: mostly on screen, in a tab that is in front.
  useSeenElement(messageRef, visible && inline && Boolean(markDraftSeen), markDraftSeen ?? noop);

  useEffect(() => {
    const message = messageRef.current;

    if (!visible || !message) return undefined;
    let frame = 0;
    const reveal = () => {
      globalThis.cancelAnimationFrame(frame);
      frame = globalThis.requestAnimationFrame(() => revealPlanningConfirmation(message));
    };

    reveal();
    const observer = globalThis.ResizeObserver ? new globalThis.ResizeObserver(reveal) : null;

    observer?.observe(message);
    const panel = message.closest<HTMLElement>('.ai-overview-panel, .planning-messages');

    if (panel) observer?.observe(panel);
    document.querySelectorAll('.input-section').forEach((element) => observer?.observe(element));

    return () => {
      globalThis.cancelAnimationFrame(frame);
      observer?.disconnect();
      panel?.style.removeProperty('--confirmation-composer-overlap');
    };
  }, [visible]);

  if (!visible || !context?.confirmation) return null;

  return (
    <div ref={messageRef} className="ai-overview-item-wrapper planning-start-message">
      <ProjectTitleConfirmation controller={context.confirmation} />
    </div>
  );
};
